import { Button, Input, message, Popconfirm } from 'antd';
import moment from 'moment';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import useGlobalAntStyles from '../../../../hooks/use-global-ant-styles';
import { IPayment } from '../../../../interfaces';
import { setPromoDiscount, userContext, workspaceContext } from '../../../../state';
import { openWebsitePricing } from '../../../../utils/open-site';
import { requestUser, requestWorkspace, sendActionAnalytics } from '../../../common/api';
import LoadingPage from '../../../common/loading-page';
import { requestPromoDiscount } from '../../../pricing/api';
import { CARD_TYPE, CardType, IPromoDiscount, IPromoGifts, PaymentType } from '../../../pricing/interfaces';
import {
  submitStripeIsCardActive,
  submitYookassaIsCardActive,
  deleteBillingCard,
  deleteBillingPayPal,
  deletePaddleBillingCard,
  deleteSigmaBillingCard,
  requestPaymentsWorkspace,
  sendPromo,
} from '../../api';
import { TabWrapper } from '../../menu/tab-wrapper';
import { PageTitle } from '../../styles';
import {
  BlockInformationPay,
  ButtonChange,
  Hr,
  NamePaymentMethod,
  PaymentMethodsActionsContainer,
  Row,
  Subtitle,
  TextEmail,
  TextGhostPayment,
  TextNameTarif,
} from '../domElements/billing';
import PaymentsTable from '../payments-table';
import PromoDiscountView from '../promo/discount-data';
import PromoActivatedGifts from '../promo/gifts-data';
import { ActivatedGiftsContainer } from '../promo/styles';
import InvoiceInput from './invoice-input';
import { useUserBasicInfo } from '../../../../state/user/user-basic-info.atom';
import { E_ANALYTICS_ACTIONS, E_ANALYTICS_ACTIONS_INFO, E_ANALYTICS_INTERFACE_AREA } from '../../../../../common/constants/analytics';
import CancelSubscriptionQuizModal from '../../../common/modals/cancel-subscription-quiz';

const BillingPersonalAreaPage: FC = () => {
  const [isCancelSubscriptionQuizModalVisible, setIsCancelSubscriptionQuizModalVisible] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [payments, setPayments] = useState<IPayment[]>([]);
  const [promo, setPromo] = useState<string>('');
  const [isPromoLoading, setIsPromoLoading] = useState<boolean>(false);
  const [promoGiftsData, setPromoGiftsData] = useState<IPromoGifts>({} as IPromoGifts);
  const [promoDiscountData, setPromoDiscountData] = useState<IPromoDiscount>(
    {
      discount: {
        active: false,
        discountType: 'percent',
        value: 0,
      },
    },
  );

  const workspace = useContext(workspaceContext);

  const unsubscribeFnRef = useRef<() => Promise<void>>();

  const {
    _id: userId,
    card = {},
    paypalSubscriptionId,
    isQuickSettingsEnabled,
    updateUser,
  } = useContext(userContext);

  const {
    id: workspaceId,
    planId: workspacePlanId,
    planName: workspacePlanName,
    profilesCount: workspaceProfilesCount,
    planProfilesMax: workspaceMaxProfiles,
    isLoaded: isWorkspaceLoaded,
    updateWorkspace,
  } = useContext(workspaceContext);

  const { email } = useUserBasicInfo();

  const { t: translation } = useTranslation();

  const { areAntStylesLoading } = useGlobalAntStyles();

  const planStr = `${workspacePlanName} (${workspaceProfilesCount}/${workspaceMaxProfiles} ${translation('personalArea.billing.profile', { count: workspaceMaxProfiles })})`;

  const loadPayments = async (): Promise<void> => {
    let newPayments: IPayment[];
    newPayments = await requestPaymentsWorkspace(workspaceId);


    newPayments.sort((curr, next) => {
      const firstDate = moment(curr.expiresIn).utc();
      const secondDate = moment(next.expiresIn).utc();

      return firstDate < secondDate ? 1 : -1;
    });

    setPayments(newPayments);
    const { card: userCard = {}, subscription = {} } = await requestUser().catch(() => ({}));
    updateUser({ card: userCard, subscription });
  };

  useEffect(() => {
    if (!(email && isWorkspaceLoaded)) {
      return;
    }

    loadPayments().then(() => setIsLoading(false));
  }, [email, isQuickSettingsEnabled, workspaceId, workspacePlanId, isWorkspaceLoaded]);

  const showModalCancelSubscriptionQuiz = (unsubscribeFn: () => Promise<void>) => {
    setIsCancelSubscriptionQuizModalVisible(true);
    unsubscribeFnRef.current = unsubscribeFn;
  };
  
  const hideModalCancelSubscriptionQuiz = () => {
    setIsCancelSubscriptionQuizModalVisible(false);
  };

  const showPaymentHistory = (): JSX.Element | null => {
    if (!card.brand && card.type === 'sigma_card') {
      card.brand = 'Card';
    }

    const hasPaymentMethod = card.brand || paypalSubscriptionId;
    if (!hasPaymentMethod) {
      return null;
    }

    return (
      <>
        <TextGhostPayment>
          <Trans i18nKey={'Payment method'} />
        </TextGhostPayment>
        {showPaymentCard()}
        {showPaymentPaypal()}
        <Hr style={{ marginTop: 15 }} />
      </>
    );
  };

  const ucFirst = (str = PaymentType.Card): string => str[0].toUpperCase() + str.slice(1);

  const showPaymentCard = (): JSX.Element | null => {
    if (!card.brand) {
      return null;
    }

    const getDeleteCardFn = (cardType: CardType): () => Promise<void> => {
      if (cardType === CARD_TYPE.SIGMA) {
        return deleteSigmaBillingCard;
      }

      if (cardType === CARD_TYPE.PADDLE && userId) {
        return async () => deletePaddleBillingCard(userId, workspaceId);
      }

      return deleteBillingCard;
    };

    const updateLocallyCardDataAfterEnableSub = () => {
      updateUser({ card: { ...card, isRecurringPaymentsDisabled: false } });
      message.success(<Trans i18nKey={'Your subscription has been successfully renewed'} />);
    };

    const getEnableCardFn = (cardType: CardType): () => Promise<void> => {
      if ([CARD_TYPE.PADDLE].includes(cardType)) {
        return async () => void 0;
      }

      // юкасса
      if (cardType === CARD_TYPE.SIGMA) {
        return async () =>
          submitYookassaIsCardActive(card._id, true).then(updateLocallyCardDataAfterEnableSub);
      }

      return async () =>
        submitStripeIsCardActive(card._id, true).then(updateLocallyCardDataAfterEnableSub);
    };

    const getDisableCardFn = (cardType: CardType): () => Promise<void> => {
      if ([CARD_TYPE.PADDLE].includes(cardType)) {
        return async () => void 0;
      }

      // юкасса
      if (cardType === CARD_TYPE.SIGMA) {
        return async () =>
          submitYookassaIsCardActive(card._id, false)
            .then(() => updateUser({ card: { ...card, isRecurringPaymentsDisabled: true } }));
      }

      return async () =>
        submitStripeIsCardActive(card._id, false)
          .then(() => updateUser({ card: { ...card, isRecurringPaymentsDisabled: true } }));
    };

    const deleteCard = getDeleteCardFn(card.type);
    const restartSubscribtionFn = getEnableCardFn(card.type);
    const pauseSubscriptionFn = getDisableCardFn(card.type);

    return (
      <>
        <Row style={{ marginTop: 30 }}>
          <BlockInformationPay>
            <NamePaymentMethod>
              {ucFirst(card.brand)}
            </NamePaymentMethod>
            <TextEmail>
              {card.lastNumbers}
            </TextEmail>
          </BlockInformationPay>
          <PaymentMethodsActionsContainer>
            {![CARD_TYPE.PADDLE].includes(card.type) && !card.isRecurringPaymentsDisabled ?
              <ButtonChange 
                style={{ marginTop: 2, marginLeft: 0 }}
                onClick={() => showModalCancelSubscriptionQuiz(pauseSubscriptionFn)}
              >
                <Trans i18nKey={'Cancel subscription'} />
              </ButtonChange> :
              null
            }
            {card.isRecurringPaymentsDisabled ?
              <ButtonChange 
                style={{ marginTop: 2, marginLeft: 0 }}
                onClick={() => restartSubscribtionFn()}
              >
                <Trans i18nKey={'Restart subscription'} />
              </ButtonChange> :
              null
            }
            {card.isRecurringPaymentsDisabled || [CARD_TYPE.PADDLE].includes(card.type) ?
              <Popconfirm
                placement='top'
                title={<Trans i18nKey={'Are you sure to delete this card?'} />}
                onConfirm={(): Promise<void> => deleteCard().then(() => updateUser({ card: {} }))}
                okText={<Trans i18nKey={'Yes'} />}
                cancelText={<Trans i18nKey={'No'} />}
              >
                <ButtonChange style={{ marginTop: 2, marginLeft: 0 }}>
                  <Trans i18nKey={'Delete payment method'} />
                </ButtonChange>
              </Popconfirm> :
            null
          }
          </PaymentMethodsActionsContainer>
        </Row>
      </>
    );
  };

  const showPaymentPaypal = (): JSX.Element | null => {
    if (!paypalSubscriptionId) {
      return null;
    }

    return (
      <Row style={{ marginTop: 30 }}>
        <BlockInformationPay>
          <NamePaymentMethod>
            PayPal
          </NamePaymentMethod>
          <TextEmail>
            {paypalSubscriptionId}
          </TextEmail>
        </BlockInformationPay>
        <Popconfirm
          placement='top'
          title={<Trans i18nKey={'Are you sure to delete this card?'} />}
          onConfirm={(): Promise<void> => deleteBillingPayPal(paypalSubscriptionId).then(() => updateUser({ paypalSubscriptionId: '' }))}
          okText={<Trans i18nKey={'Yes'} />}
          cancelText={<Trans i18nKey={'No'} />}
        >
          <ButtonChange style={{ width: 100, marginLeft: 114, marginTop: 2 }}>
            <Trans i18nKey={'Delete payment method'} />
          </ButtonChange>
        </Popconfirm>
      </Row>
    );
  };

  const changPlanClick = (e: any) => {
    sendActionAnalytics(E_ANALYTICS_ACTIONS.clickedBuyPlan, {
      actionInfo: E_ANALYTICS_ACTIONS_INFO.billingPlans,
      interfaceArea: E_ANALYTICS_INTERFACE_AREA.billingPlans,
    });

    e.preventDefault();
    openWebsitePricing({
      workspaceId,
      isQuickSettingsEnabled,
      isShowPaymentMethods: true,
    });
  };

  useEffect(() => {
    requestPromoDiscount({ workspaceId: workspace.id }).then(setPromoDiscountData);
  }, [workspace.id]);

  const onPromoInsert = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setPromo(event.target.value.toUpperCase());
  };

  const checkPromo = async (): Promise<void> => {
    setPromoGiftsData({} as IPromoGifts);
    setIsPromoLoading(true);
    try {
      const result = await sendPromo(promo, workspaceId).finally(() => {
        setPromo('');
        setIsPromoLoading(false);
      });

      const { discount: promoDiscount } = result;
      setPromoDiscount(promoDiscount);

      message.success(<Trans i18nKey={'Your promo code has been activated'} />);
      setPromoGiftsData(result);

      if (result.period.amount) {
        const { planExpiresAt } = await requestWorkspace(workspaceId);
        updateWorkspace({ planExpiresAt });
      }
    } finally {
      setPromo('');
      setIsPromoLoading(false);
      setIsLoading(false);
    }
  };

  const sortPaymentsByCreatedDateDesc = (prev: IPayment, next: IPayment): number => {
    if (prev.createdAt > next.createdAt) {
      return -1;
    }

    if (prev.createdAt < next.createdAt) {
      return 1;
    }

    return 0;
  };

  const sortedPayments = useMemo(() => [...payments.sort(sortPaymentsByCreatedDateDesc)], [payments]);

  if (areAntStylesLoading) {
    return <LoadingPage />;
  }

  if (isLoading) {
    return (
      <TabWrapper tabKey='billing'>
        <PageTitle>
          <Trans i18nKey={'Billing Plans'} />
        </PageTitle>
        <LoadingPage />
      </TabWrapper>
    );
  }

  return (
    <TabWrapper tabKey='billing'>
      <PageTitle>
        <Trans i18nKey={'Billing Plans'} />
      </PageTitle>
      <Row style={{ marginTop: 22 }}>
        <TextNameTarif>
          {planStr}
        </TextNameTarif>
        <ButtonChange onClick={changPlanClick}>
          <Trans i18nKey={'Change plan'} />
        </ButtonChange>
      </Row>
      <Hr style={{ marginTop: 11 }} />
      {showPaymentHistory()}
      <Subtitle style={{ paddingTop: 30 }}>
        <Trans i18nKey={'Payment history'} />
      </Subtitle>
      <div>
        <PaymentsTable
          payments={sortedPayments}
          isQuickSettingsEnabled={isQuickSettingsEnabled}
        />
      </div>
      <Subtitle>
        <Trans i18nKey={'Coupon'} />
      </Subtitle>
      <Row
        style={{
          alignItems: 'center',
          marginTop: 20,
        }}
      >
        <Input
          style={{
            width: 202,
          }}
          value={promo}
          onChange={onPromoInsert}
          placeholder={translation('personalArea.billing.placeholderInputEnterCode') || ''}
        />
        <Button
          disabled={!promo}
          loading={isPromoLoading}
          onClick={checkPromo}
          type='default'
          style={{
            backgroundColor: 'var(--F7F7F8-personal-area-billing)',
            marginLeft: 8,
          }}
        >
          <Trans i18nKey={'Apply'} />
        </Button>
      </Row>
      <InvoiceInput />
      <ActivatedGiftsContainer>
        <PromoDiscountView discountData={promoDiscountData.discount} />
      </ActivatedGiftsContainer>
      <ActivatedGiftsContainer>
        <PromoActivatedGifts promoGiftsData={promoGiftsData} />
      </ActivatedGiftsContainer>
      <CancelSubscriptionQuizModal
        isVisible={isCancelSubscriptionQuizModalVisible}
        hideModal={hideModalCancelSubscriptionQuiz}
        unsubscribe={unsubscribeFnRef.current}
      />
    </TabWrapper>
  );
};

export default BillingPersonalAreaPage;
